import React from 'react';

import { Group } from 'components/shared/Group';
import { GroupMembers } from 'components/shared/Group/GroupMembers';
import { SkeletonList } from './SkeletonList';

import { useQuery } from '@apollo/react-hooks';
import { useTranslation } from 'hooks';
import { OrganizationTypes, GroupTypes } from 'helpers';
import { GroupListQuery } from 'services';

interface GroupListProps {
  /** @string - Search query that filters the user list */
  search?: string;
}

interface GroupsListData {
  me: {
    groups: Group[];
    organization: Organization;
    orgGroup: Group;
    userProfile: User;
    otherPeople: User[];
  };
}

export const GroupList: React.FC<GroupListProps> = ({ search }) => {
  const { t } = useTranslation();
  const { error, data } = useQuery<GroupsListData>(GroupListQuery, {
    fetchPolicy: 'cache-and-network',
  });

  if (data === undefined) {
    return <SkeletonList />;
  }

  if (error) return <SkeletonList />;

  const IS_WORK_ACCOUNT = data.me.organization.type === OrganizationTypes.WORK;

  /**
   * Returns all user contacts that are searchable
   */
  const getUserContacts = () => {
    const otherPeople = data!.me.otherPeople || [];
    const topContacts = data!.me.userProfile.topContacts?.contacts || [];
    const organizationContacts = data!.me.orgGroup?.members || [];

    if (IS_WORK_ACCOUNT) {
      return [...organizationContacts, ...otherPeople];
    } else {
      return topContacts;
    }
  };

  /**
   * Renders the search member list
   * Will hide the groups list
   */
  const renderSearch = () => {
    const currentUser = data.me.userProfile;
    const contactList = getUserContacts();
    const parsedSearch = search!.toLowerCase().trim();

    const searchResults = contactList.filter((user: User) => {
      const parsedName = user.name ? user.name.toLowerCase().trim() : '';
      const parsedEmail = user.email ? user.email.toLowerCase().trim() : '';

      const matchesName = parsedName.indexOf(parsedSearch) > -1;
      const matchesEmail = parsedEmail.indexOf(parsedSearch) > -1;

      return (matchesName || matchesEmail) && user.id !== currentUser.id;
    });

    return <GroupMembers members={searchResults} />;
  };

  /**
   * Renders other people group
   */
  const renderOtherPeople = () => {
    const groupTitle = IS_WORK_ACCOUNT
      ? t('groups-sidebar.other-people')
      : t('groups-sidebar.people');

    const otherPeopleContacts = IS_WORK_ACCOUNT
      ? data.me.otherPeople || []
      : data.me.userProfile.topContacts?.contacts || [];

    return (
      <Group
        group={{
          id: 'other_people',
          name: groupTitle,
          members: otherPeopleContacts,
        }}
        currentUserId={data!.me.userProfile.id}
        isOtherPeople
      />
    );
  };

  /**
   * Renders organization group
   */
  const renderOrgGroup = () => {
    return (
      <Group
        group={{
          id: data.me.organization.id!,
          groupType: GroupTypes.ORGANISATION,
          name: data.me.organization.name,
          members: data.me.orgGroup.members,
        }}
        currentUserId={data!.me.userProfile.id}
        isOrganization
      />
    );
  };

  /**
   * Renders the list of groups
   * Will not render if the user is searching
   */
  const renderGroups = () => {
    return (
      <>
        {IS_WORK_ACCOUNT ? (
          <>
            {/* Organization Group */}
            {renderOrgGroup()}

            {/* Other People */}
            {renderOtherPeople()}
          </>
        ) : (
          <>
            {/* People List */}
            {renderOtherPeople()}
          </>
        )}
      </>
    );
  };

  return <>{!!search ? renderSearch() : renderGroups()}</>;
};

export default GroupList;
